import { createSlice, isFulfilled, isPending, type PayloadAction } from '@reduxjs/toolkit';

import type { CampaignPriceChangeReason } from '@/interface/campaign';
import { readCampaignChargeRefusal } from '@/utils/campaign-charge-refusal';

export interface CampaignChargeState {
  /** The cause the CHARGE reported, from the checkout response. */
  refusedReason: CampaignPriceChangeReason | null;
  /** The cause the visitor confirmed at the price gate, so the same refusal is not announced twice. */
  acknowledgedReason: CampaignPriceChangeReason | null;
}

const initialState: CampaignChargeState = {
  refusedReason: null,
  acknowledgedReason: null,
};

/**
 * Whether the action belongs to a charge attempt, read off the thunk's own type string — every
 * checkout thunk in the app is named `app…Checkout…/…`. Matching on the name for the same reason the
 * fulfilled matcher watches everything: a list of thunks here would go stale the day someone adds a
 * gateway, and the cost of a false positive is one cleared warning, not a wrong one.
 */
function isChargeAttempt(type: string): boolean {
  return type.toLowerCase().includes('checkout');
}

/**
 * Where a campaign discount refused at charge time lands, for whoever can still tell the visitor.
 *
 * ⚠️ It listens with `addMatcher(isFulfilled)` — every fulfilled thunk in the app, not a list of
 * checkout thunks — and that is the point. The charge response is handled in twenty-odd gateway
 * components across a dozen slices; enumerating them would mean this feature quietly missing a
 * gateway the day someone adds one. `readCampaignChargeRefusal` answers `null` for anything that is
 * not a campaign refusal, so every other action passes through untouched.
 *
 * It lives in Redux rather than `sessionStorage` because the store is a module singleton created in
 * `_app`, so it survives the `router.push('/thanks-for-purchase')` that the card flows do — which is
 * exactly where the notice has to appear. A gateway that leaves the site entirely and comes back on
 * a full load loses it; those are also the flows whose charge does not answer us at all.
 *
 * ⚠️ Nothing that DISPLAYS the refusal may clear it. The notice is mounted twice — inside card 3 and
 * on `/thanks-for-purchase` — and the card flows commit this state while the visitor is still on
 * `/pricing`, so a notice that consumed on mount would leave the thanks page with nothing to show.
 * The refusal's life ends at the next charge attempt (the pending matcher below), which is also what
 * keeps a second purchase in the same session from inheriting the first one's warning.
 */
const campaignChargeSlice = createSlice({
  name: 'campaignCharge',
  initialState,
  reducers: {
    rememberAcknowledgedCampaignCause(state, action: PayloadAction<CampaignPriceChangeReason>) {
      // eslint-disable-next-line no-param-reassign
      state.acknowledgedReason = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isPending, (state, action) => {
        if (!isChargeAttempt(action.type)) {
          return;
        }

        // A recusa descreve a cobrança anterior; daqui em diante existe uma nova. Só a recusa: a
        // causa reconhecida continua valendo, senão uma segunda cobrança com a MESMA causa voltaria
        // a avisar quem já reconfirmou.
        // eslint-disable-next-line no-param-reassign
        state.refusedReason = null;
      })
      .addMatcher(isFulfilled, (state, action) => {
        const reason = readCampaignChargeRefusal(action.payload);

        if (reason === null) {
          return;
        }

        // A dedupe é decidida na chegada, e não a cada render: a causa que o visitante reconfirmou
        // no portão produziria a mesma frase duas vezes para um evento só. Avaliá-la na tela deixaria
        // a recusa guardada para sempre e a faria reaparecer quando a causa reconhecida mudasse.
        // eslint-disable-next-line no-param-reassign
        state.refusedReason = reason === state.acknowledgedReason ? null : reason;
      });
  },
});

export const { rememberAcknowledgedCampaignCause } = campaignChargeSlice.actions;

export default campaignChargeSlice.reducer;
